import React, { Component, ReactElement, ReactNode } from 'react';
import {
  Page,
  TextField,
  Checkbox,
  Heading,
  Dropdown,
  TextStyle
} from '@superops/ui-design-system';
import {
  HeaderTag,
  preFormatForm,
  validateForm,
  assetViewConstraints,
  reactSelectInlineError,
  getTranslatedText,
  SaveCard,
  AccessControl,
  TEXT_LENGTH,
  WithCard,
  EventEmitter,
  Loader
} from '@nile/components/src';
import AssetViewRuleBuilder from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-view-rule-builder/RuleBuilder';

import { useQuery } from '@apollo/client';
import { cloneDeep, isEmpty } from 'lodash';
import { injectIntl } from 'react-intl';
import { ASSET_TYPES } from '@nile/rmm/src/constants';
import { RMMClient } from '@nile/graphql';
import {
  GET_MONITORED_ASSET_META_INFO,
  GET_ASSET_TYPES,
  GET_WORKSTATION_ASSET_CLASS_LIST
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createSoftwareVersionValidator } from '@nile/rmm/src/modules/home/components/sub-pages/utils/utils';

const getAssetViewValue = (tabState, data) => {
  if (!isEmpty(tabState.state)) {
    return tabState.state.asset;
  }
  return (
    data || {
      name: '',
      viewCondition: {},
      shared: false,
      viewSubType: 'WORKSTATION_ASSET'
    }
  );
};

const getFormatDropdownOption = (data) => {
  if (!data) return [];

  return data?.map((el: { name: string; classId: string }) => ({
    label: el.name,
    value: el.classId,
    name: el.name
  }));
};

class AssetViewSheet extends Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: {},
      assetViewData: getAssetViewValue(props?.context?.activeTab, props.data),
      prefilledRule: null
    };

    const dependentAttributes = (attr: string) =>
      attr === 'paddedSoftwareVersion' ||
      attr === 'filteredSoftware.paddedSoftwareVersion';
    const requiredAttributes = (attr: string) =>
      attr === 'filteredSoftware.softwareName' || attr === 'softwareName';

    this.validateSoftwareVersion = createSoftwareVersionValidator({
      intl: props.intl,
      setPrefilledRule: this.setPrefilledRule,
      dependentAttributes,
      requiredAttributes,
      errorMessageId: 'rmm.filter.softwareVersion.dependency.error',
      prefilledRule: {
        attribute: 'filteredSoftware.softwareName',
        value: '',
        forceAndOperator: true
      }
    });
  }

  componentDidMount() {
    EventEmitter.subscribe('tabChange', this.updateTabState);
  }

  componentWillUnmount() {
    EventEmitter.unsubscribe('tabChange');
  }

  updateTabState = (eventData: Object) => {
    this.props.context.updateTabState(
      {
        asset: this.state.assetViewData
      },
      eventData
    );
  };

  setPrefilledRule = (rule: any) => {
    this.setState({
      prefilledRule: rule
    });
  };

  handleValidation = () => {
    const { name, viewCondition, viewSubType } = this.state.assetViewData;
    const data = {
      name,
      viewSubType
      // viewCondition
    };
    const validationResult = validateForm(
      { ...preFormatForm(data), viewCondition },
      assetViewConstraints
    );

    return validationResult;
  };

  handleSave = () => {
    const validationResult = this.handleValidation();

    if (validationResult) {
      this.setState({
        errors: validationResult
      });
      return;
    }

    // Validate software version dependency using the validator
    const isValid = this.validateSoftwareVersion(
      this.state.assetViewData.viewCondition
    );

    if (!isValid) {
      // Validation failed, prefilledRule is set by the validator
      return;
    }

    this.setState({
      errors: {}
    });

    if (isEmpty(validationResult)) {
      this.props.saveAction(this.state.assetViewData);
    }
  };

  handleChange = (name) => {
    const data = cloneDeep(this.state.assetViewData);
    data.name = name;
    this.setState({
      assetViewData: data
    });
  };

  onRuleChange = (updatedRules) => {
    const data = cloneDeep(this.state.assetViewData);
    data.viewCondition = updatedRules;
    this.setState({
      assetViewData: data
    });
  };

  onRulePrefilled = () => {
    this.setState({
      prefilledRule: null
    });
  };

  handleSharedViewChange = (selected) => {
    const data = cloneDeep(this.state.assetViewData);
    data.shared = selected;
    this.setState({
      assetViewData: data
    });
  };

  handleAssetTypeChange = (value: string) => {
    const data = cloneDeep(this.state.assetViewData);
    data.viewSubType = value;
    data.viewCondition = {};
    this.setState({
      assetViewData: data
    });
  };

  back = () => {
    this.props?.context.closeTab();
  };

  render() {
    const { assetViewData, errors, prefilledRule } = this.state;
    const { intl, actionProgress, title, data, flags, meta } = this.props;
    const actions = {
      back: this.back
    };

    const ruleBuilderContext = {
      updateAutomationRule: this.onRuleChange
    };

    const dependentFields = [
      ['paddedSoftwareVersion', 'filteredSoftware.paddedSoftwareVersion'],
      ['filteredSoftware.softwareName', 'softwareName']
    ];

    return (
      <div className="p20 full-height">
        <Page
          title={
            <Heading element="h1">{getTranslatedText(intl, title)}</Heading>
          }
          variant="primary"
          contentHeight="80px"
          primaryAction={
            <SaveCard
              actions={actions}
              handleSave={this.handleSave}
              loading={actionProgress}
              isNewLayout
            />
          }
        >
          <AssetViewForm
            textFieldLabel={getTranslatedText(
              intl,
              'rmm.assetView.assetViewName.header'
            )}
            renderTextField={
              <TextField
                placeholder={getTranslatedText(
                  intl,
                  'rmm.assetView.assetViewName.placeholder'
                )}
                value={assetViewData.name}
                onChange={this.handleChange}
                error={errors?.name}
                maxLength={TEXT_LENGTH.size_3}
              />
            }
            dropdownLabel="Asset types"
            renderDropdown={({ options, loading }) => (
              <Dropdown
                isLoading={loading}
                value={
                  options?.find(
                    (el) => el.value === assetViewData?.viewSubType
                  ) || []
                }
                isDisabled={!isEmpty(data)}
                label={'Asset types'} //TODO: FIX
                options={options || []}
                classNamePrefix="value_selector"
                onChange={(option) => {
                  this.handleAssetTypeChange(option.value);
                }}
                menuPlacement="auto"
                placeholder={'Select asset Type'} // TODO: Fix
                menuPortalTarget={document.body}
              />
            )}
            renderSharedCheckbox={
              <Checkbox
                label={getTranslatedText(intl, 'asset.patch.new.sharedView')}
                checked={assetViewData.shared}
                onChange={this.handleSharedViewChange}
              />
            }
            ruleBuilderHeader={getTranslatedText(
              intl,
              'rmm.assetView.ruleBuilder.header'
            )}
            getMonitoredAssetsMetaInfoQueryVariable={{
              viewType: isEmpty(assetViewData.viewSubType)
                ? 'WORKSTATION_ASSET'
                : assetViewData.viewSubType,
              version: 1
            }}
            renderAssetViewRuleBuilder={
              <AssetViewRuleBuilder
                meta={meta}
                allEntities={this.props?.allEntities}
                allOperators={this.props?.allOperators}
                query={
                  isEmpty(assetViewData?.viewCondition)
                    ? null
                    : assetViewData.viewCondition
                }
                errors={errors}
                context={ruleBuilderContext}
                intl={intl}
                currentModule={ASSET_TYPES?.find(
                  (el) => el.name === assetViewData?.viewSubType
                )}
                prefilledRule={prefilledRule}
                onRulePrefilled={this.onRulePrefilled}
                dependentFields={dependentFields}
              />
            }
            error={
              typeof errors?.['viewCondition.operands']?.[0] === 'string' &&
              reactSelectInlineError(errors?.['viewCondition.operands'])
            }
          />
        </Page>
      </div>
    );
  }
}

export default injectIntl(AssetViewSheet);

export function AssetViewForm({
  textFieldLabel,
  renderTextField,
  dropdownLabel,
  renderDropdown,
  renderSharedCheckbox,
  ruleBuilderHeader,
  getMonitoredAssetsMetaInfoQueryVariable,
  renderAssetViewRuleBuilder,
  error,
  assetViews = true
}: {
  // eslint-disable-next-line react/require-default-props
  textFieldLabel?: ReactNode;
  renderTextField: ReactNode;
  dropdownLabel: ReactNode;
  renderDropdown: (arg: { options: any; loading: boolean }) => ReactNode;
  renderSharedCheckbox: ReactNode;
  ruleBuilderHeader: ReactNode;
  getMonitoredAssetsMetaInfoQueryVariable: {
    viewType: string;
    version: number;
    viewModule?: string;
  };
  renderAssetViewRuleBuilder: ReactElement;
  error: ReactNode;
}) {
  const flags = useFlags();

  const {
    loading: assetTypesLoading,
    error: assetTypesError,
    data: assetTypes
  } = useQuery(GET_ASSET_TYPES, {
    client: RMMClient,
    skip: !assetViews
  });

  const {
    loading: workStationAssetClassListLoading,
    error: workStationAssetClassListError,
    data: workStationAssetClassList
  } = useQuery(GET_WORKSTATION_ASSET_CLASS_LIST, {
    client: RMMClient,
    skip: assetViews
  });

  const {
    loading: metaInfoLoading,
    error: metaInfoError,
    data: metaInfoData
  } = useQuery(GET_MONITORED_ASSET_META_INFO, {
    client: RMMClient,
    fetchPolicy: 'cache-and-network',
    variables: getMonitoredAssetsMetaInfoQueryVariable,
    skip: !flags.networkMonitoringAssetViews
  });

  return (
    <>
      <WithCard isNewLayout customClassName="mb-10">
        {textFieldLabel && <HeaderTag title={textFieldLabel} isNewLayout />}

        <div>{renderTextField}</div>

        {flags.networkMonitoringAssetViews && (
          <div className="mt-20 mb-20">
            <TextStyle weight="SemiBold">{dropdownLabel}</TextStyle>
            <div className="mt-10 max-width-200">
              {assetTypesError || workStationAssetClassListError ? (
                <h1>Error...</h1>
              ) : (
                renderDropdown({
                  options: assetViews
                    ? assetTypes?.getAssetViewTypes
                    : getFormatDropdownOption(
                      workStationAssetClassList?.getWorkStationAssetClassList
                    ),
                  loading: assetTypesLoading || workStationAssetClassListLoading
                })
              )}
            </div>
          </div>
        )}
      </WithCard>

      {renderSharedCheckbox && (
        <AccessControl
          roleToCheck="assetSharedViews.allow"
          render={<WithCard isNewLayout>{renderSharedCheckbox}</WithCard>}
        />
      )}

      <WithCard isNewLayout customClassName="mt-30">
        <HeaderTag title={ruleBuilderHeader} isNewLayout />

        {flags.networkMonitoringAssetViews ? (
          metaInfoError ? (
            <h1>Error...</h1>
          ) : metaInfoLoading || !metaInfoData ? (
            <Loader />
          ) : (
            React.cloneElement(renderAssetViewRuleBuilder, {
              meta: metaInfoData.getMonitoredAssetsMetaInfo
            })
          )
        ) : (
          renderAssetViewRuleBuilder
        )}

        {error}
      </WithCard>
    </>
  );
}
